import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Clipboard, ExternalLink, Send } from 'lucide-react'
import { useRef, useState } from 'react'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn, copyText } from '@/lib/utils'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { invalidateQuoteOverview } from '../api/queries'
import { financingQueryOptions, invoiceErrorMessage, invoicingApi, invoicingQueryKey } from '../invoicing/api'
import type { Quote } from '../api/types'
import { newRequestId, publishingApi, type PublicationMode, type PublicationResult, type VisibleSection } from './api'

const sections: Array<{ value: VisibleSection; label: string }> = [
  { value: 'equipment', label: 'Equipment' },
  { value: 'services', label: 'Services' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'fulfillment', label: 'Availability' },
  { value: 'notes', label: 'Notes' },
  { value: 'terms', label: 'Terms' },
]

const linkDurations = [
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '2160', label: '90 days' },
] as const

/** A final quote always shows everything; a draft shows what Sales picks. */
function sectionsForMode(mode: PublicationMode, current: VisibleSection[]) {
  return mode === 'final' ? sections.map((section) => section.value) : current
}

function publishError(error: unknown, mode: PublicationMode) {
  if (error instanceof QuoteBuilderApiError) {
    if (error.code === 'stale_revision') return 'The quote changed. Close this window and publish again.'
    if (error.code === 'below_umrp') return 'A price is below the manufacturer’s minimum. Fix it in Pricing first.'
    if (error.code === 'quote_not_ready_for_publication') {
      const errors = (error.body.detail as { errors?: string[] } | undefined)?.errors
      return errors?.length ? errors.join(' ') : 'Finish the Review stage before publishing the final quote.'
    }
  }
  return mode === 'final'
    ? 'The final quote could not be published. Check the Review stage and try again.'
    : 'The draft could not be published. Try again.'
}

function sendError(error: unknown) {
  if (error instanceof QuoteBuilderApiError && error.code === 'customer_email_required') {
    return 'Add the customer’s email before sending.'
  }
  return 'The email could not be sent. Check the quote and customer, then try again.'
}

/**
 * Publish the current revision to the customer's link, then email it or copy it (demo
 * `PublishQuoteAction`). Drafts come from the quote bar; the Review stage passes `publicationMode="final"`.
 */
export function PublishQuoteAction({
  quote,
  publicationMode = 'draft',
  triggerLabel = 'Publish draft',
  triggerClassName,
  disabled: disabledByCaller = false,
  disabledReason: callerReason,
}: {
  quote: Quote
  publicationMode?: PublicationMode
  triggerLabel?: string
  triggerClassName?: string
  disabled?: boolean
  disabledReason?: string
}) {
  // A started project is cut from its accepted final quote; nothing new is published after that.
  const started = quote.status === 'started'
  const disabled = disabledByCaller || started
  const disabledReason = started ? 'The project has started.' : callerReason
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState<VisibleSection[]>(['equipment', 'services', 'fulfillment'])
  const [expires, setExpires] = useState<(typeof linkDurations)[number]['value']>('720')
  const [result, setResult] = useState<PublicationResult | null>(null)
  const [copied, setCopied] = useState(false)
  // The demo's "Send invoice": publishing the final quote also issues and emails its first invoice.
  const [sendInvoice, setSendInvoice] = useState(false)
  const invoiceRequest = useRef<string | null>(null)
  const operation = useRef<string | null>(null)
  const deliveryRequest = useRef<string | null>(null)
  const selectedSections = sectionsForMode(publicationMode, visible)
  const financing = useQuery({ ...financingQueryOptions(api, quote.id), enabled: open && publicationMode === 'final' })
  const prepaid = financing.data?.financing?.term_type === 'prepaid'
  const withInvoice = publicationMode === 'final' && sendInvoice && !prepaid

  const refreshCustomerViews = () => {
    void queryClient.invalidateQueries({ queryKey: ['quote-activity', quote.id] })
    void queryClient.invalidateQueries({ queryKey: ['quote-publications', quote.id] })
    void invalidateQuoteOverview(queryClient)
  }
  const publish = useMutation({
    retry: false,
    mutationFn: () => {
      operation.current ??= newRequestId()
      return publishingApi.publish(api, quote, publicationMode, selectedSections, Number(expires), operation.current)
    },
    onSuccess: (next) => {
      setResult(next)
      refreshCustomerViews()
      // Any publication changes whether the first invoice can be created (USA-1336).
      void queryClient.invalidateQueries({ queryKey: invoicingQueryKey(quote.id) })
      if (next.mode === 'final' && withInvoice) invoice.mutate()
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['quote', quote.id] })
      void queryClient.invalidateQueries({ queryKey: ['quote-workflow', quote.id] })
    },
  })
  const invoice = useMutation({
    retry: false,
    mutationFn: async () => {
      const invoices = invoicingApi(api)
      const current = await invoices.read(quote.id)
      const created = current.invoice?.finalized_at ? current : await invoices.create(quote.id, current.quote_revision)
      if (created.invoice?.sent_at) return created
      invoiceRequest.current ??= newRequestId()
      return invoices.send(quote.id, created.quote_revision, invoiceRequest.current, 1)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: invoicingQueryKey(quote.id) })
      void queryClient.invalidateQueries({ queryKey: ['quote-workflow', quote.id] })
      refreshCustomerViews()
    },
  })
  const delivery = useMutation({
    retry: false,
    mutationFn: () => {
      deliveryRequest.current ??= newRequestId()
      return publishingApi.send(api, quote, result!.publication_number, deliveryRequest.current)
    },
    onSuccess: refreshCustomerViews,
  })
  const busy = publish.isPending || delivery.isPending || invoice.isPending

  const onOpenChange = (next: boolean) => {
    if (busy) return
    setOpen(next)
    if (!next) {
      publish.reset()
      delivery.reset()
      invoice.reset()
      invoiceRequest.current = null
      setResult(null)
      setCopied(false)
      operation.current = null
      deliveryRequest.current = null
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button className={triggerClassName} disabled={disabled} size="sm" title={disabled ? disabledReason : undefined}>
          <Send aria-hidden="true" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{publicationMode === 'final' ? 'Publish final quote' : 'Publish draft'}</DialogTitle>
          <DialogDescription>
            {quote.quote_number} · Revision {quote.revision}
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="grid gap-4">
            <div className="flex items-center gap-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-emerald-600 text-white">
                <Check aria-hidden="true" className="size-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">
                  {result.mode === 'final' ? 'Final quote published' : 'Draft published'}
                </p>
                <p className="text-xs text-muted-foreground">Publication {result.publication_number}</p>
              </div>
            </div>
            {result.mode === 'final' && (invoice.isPending || invoice.isSuccess || invoice.isError) ? (
              <div className="grid gap-2 rounded-lg border px-3 py-2 text-sm" role="status">
                {invoice.isPending ? (
                  <p className="text-muted-foreground">Creating and sending the invoice...</p>
                ) : invoice.data ? (
                  <p className="font-medium text-emerald-700">
                    Invoice sent{invoice.data.invoice?.recipient_email ? ` to ${invoice.data.invoice.recipient_email}` : ''}.
                  </p>
                ) : (
                  <>
                    <p className="text-destructive" role="alert">
                      {invoiceErrorMessage(invoice.error)}
                    </p>
                    <Button onClick={() => invoice.mutate()} size="sm" variant="outline">
                      Retry invoice
                    </Button>
                  </>
                )}
              </div>
            ) : null}
            {delivery.data ? (
              <p className="break-all text-sm font-medium text-emerald-700" role="status">
                Sent to {delivery.data.recipient_email}
              </p>
            ) : (
              <LoadingButton
                disabled={publish.isPending}
                loading={delivery.isPending}
                loadingText="Sending..."
                onClick={() => delivery.mutate()}
              >
                <Send aria-hidden="true" />
                Email to customer
              </LoadingButton>
            )}
            {delivery.error ? (
              <p className="text-sm text-destructive" role="alert">
                {sendError(delivery.error)}
              </p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                className="sm:flex-1"
                onClick={async (event) => {
                  setCopied(await copyText(result.share_url, event.currentTarget))
                }}
                variant="outline"
              >
                {copied ? <Check aria-hidden="true" /> : <Clipboard aria-hidden="true" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button asChild className="sm:flex-1" variant="outline">
                {/* eslint-disable-next-line no-restricted-syntax -- the customer portal is another app */}
                <a href={result.share_url} rel="noreferrer" target="_blank">
                  <ExternalLink aria-hidden="true" />
                  Open customer view
                </a>
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="grid gap-5"
            onSubmit={(event) => {
              event.preventDefault()
              if (!publish.isPending) publish.mutate()
            }}
          >
            {publicationMode === 'draft' ? (
              <fieldset className="grid gap-2">
                <legend className="mb-2 text-xs font-medium text-muted-foreground">Customer sees</legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {sections.map((section) => {
                    const checked = selectedSections.includes(section.value)
                    return (
                      <label
                        className={cn(
                          'flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm',
                          checked && 'border-primary/40 bg-muted',
                        )}
                        key={section.value}
                      >
                        <input
                          checked={checked}
                          className="size-4 accent-primary"
                          onChange={(event) =>
                            setVisible((current) =>
                              event.target.checked
                                ? [...current, section.value]
                                : current.filter((item) => item !== section.value),
                            )
                          }
                          type="checkbox"
                        />
                        {section.label}
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            ) : null}
            <div className="grid gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Link stays active
              </span>
              <ReviewChoiceGroup
                label="Link stays active"
                onChange={setExpires}
                options={linkDurations}
                value={expires}
              />
            </div>
            {publicationMode === 'final' && !prepaid ? (
              <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm">
                <input
                  checked={sendInvoice}
                  className="size-4 accent-primary"
                  onChange={(event) => setSendInvoice(event.target.checked)}
                  type="checkbox"
                />
                Send invoice
                <span className="text-xs text-muted-foreground">
                  Emails the first invoice
                </span>
              </label>
            ) : null}
            {disabled || publish.error ? (
              <p className="rounded-lg bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">
                {disabled ? disabledReason : publishError(publish.error, publicationMode)}
              </p>
            ) : null}
            <LoadingButton
              className="w-full"
              disabled={disabled || selectedSections.length === 0}
              loading={publish.isPending}
              loadingText="Publishing..."
              type="submit"
            >
              <Send aria-hidden="true" />
              {publicationMode === 'final' ? (withInvoice ? 'Publish final quote & send invoice' : 'Publish final quote') : 'Publish draft'}
            </LoadingButton>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
